import { DEV } from 'cc/env';
import { _decorator } from 'cc';
import GameDependency from '../../../System/GameDependency';

enum EventType {
    PLAY = 'PLAY',
    STOP = 'STOP',
    PAUSE = 'PAUSE',
    RESUME = 'RESUME',
    FINISH = 'FINISH',
}

export abstract class IPlayable extends GameDependency {
    abstract play(...params: any): Promise<any>;
    abstract stop(waitForFinish?: boolean): void;
}

const { ccclass, property } = _decorator;

@ccclass('Processor')
export default abstract class Processor extends IPlayable {
    public static readonly EVENT_TYPE = EventType;
    private static readonly STOP_SYMBOL: Symbol = Symbol('STOP');

    @property({ tooltip: DEV && 'Force replay the process even if it is running.' })
    private readonly forceReplay: boolean = false;

    private _processorName: string = '';
    private _playID: number = 0;
    private _iterator: IterableIterator<Promise<any>> = null;
    private _iteratorResult: IteratorResult<Promise<any>, void> = null;
    private _stopPromise: Promise<Symbol> = null;
    private _stopResolve: (value: Symbol) => void;
    private _pausePromise: Promise<void> = null;
    private _resumeResolve: () => void = null;
    private _finishPromise: Promise<void> = null;
    private _finishResolve: (value: void) => void;
    private _isStopping: boolean = false;

    public get processorName(): string {
        return this._processorName || this.name;
    }
    protected set processorName(value: string) {
        if (value === this._processorName) {
            return;
        }
        this._processorName = value;
    }

    public get isRunning(): boolean {
        return !!this._finishPromise;
    }

    public get isPaused(): boolean {
        return !!this._pausePromise;
    }

    public get isStopping(): boolean {
        return this._isStopping;
    }

    /**
     * 執行一個 process
     * @param process 要執行的工作
     * @returns [等待 process完成的 promise, 執行後可中斷工作的函式, 執行後可暫停工作的函式]
     */
    public static run<T>(
        process: Generator<Promise<any> | string, T, any>,
        finish: (isEndNaturely: boolean) => Promise<void> = () => Promise.resolve(),
        interruptMap: { [interruptName: string]: (...args: any[]) => any } = {},
    ): [Promise<T>, (waitForFinish?: boolean) => Promise<void>, (isPause: boolean) => void] {
        let stopResolve: (value: Symbol) => void;
        const stopPromise = new Promise<Symbol>((resolve) => {
            stopResolve = resolve;
        });
        let resumeResolve: () => void;
        let pausePromise: Promise<void> = null;

        return [
            (async () => {
                const iterator = process;
                let iteratorResult = iterator.next();
                let value: any;
                while (!iteratorResult.done) {
                    await pausePromise;
                    if (iteratorResult.value instanceof Promise) {
                        value = await Promise.race([iteratorResult.value, stopPromise]);
                        if (value === Processor.STOP_SYMBOL) {
                            break;
                        }
                        iteratorResult = iterator.next(value);
                    } else if (typeof iteratorResult.value === 'string') {
                        const nextParam = interruptMap[iteratorResult.value]?.();
                        iteratorResult = iterator.next(nextParam);
                    } else {
                        iteratorResult = iterator.next();
                    }
                }
                // 判斷 process是否被中斷或自然結束
                if (stopResolve) {
                    stopResolve(Processor.STOP_SYMBOL);
                    stopResolve = null;
                    await finish(true);
                }
                return value;
            })(),
            async (waitForFinish = true) => {
                if (!stopResolve) {
                    return;
                }
                if (pausePromise) {
                    resumeResolve();
                    pausePromise = null;
                }
                stopResolve(Processor.STOP_SYMBOL);
                stopResolve = null;
                if (waitForFinish) {
                    await finish(false);
                } else {
                    finish(false);
                }
            },
            (isPause) => {
                if (isPause) {
                    // Pause
                    if (pausePromise) {
                        return;
                    }
                    pausePromise = new Promise<void>((resolve) => {
                        resumeResolve = resolve;
                    });
                } else {
                    // Resume
                    if (!pausePromise) {
                        return;
                    }
                    resumeResolve();
                    pausePromise = null;
                }
            },
        ];
    }

    /**
     * Start the process
     */
    public async play(...params: any): Promise<any> {
        if (this.isRunning) {
            if (!this.forceReplay) {
                return this._finishPromise;
            }
            this.stop(false);
        }
        const playID = this._playID;
        this._stopPromise = new Promise<Symbol>((resolve) => {
            this._stopResolve = resolve;
        });
        this._finishPromise = new Promise<void>((resolve) => {
            this._finishResolve = resolve;
        });
        this.emit(EventType.PLAY, this.processorName);
        this._iterator = this.run(...params);
        this._iteratorResult = this._iterator.next();

        let value: any;
        while (!this._iteratorResult.done) {
            await this._pausePromise;
            value = await Promise.race([this._iteratorResult.value, this._stopPromise]);
            if (value === Processor.STOP_SYMBOL) {
                break;
            }
            this._iteratorResult = this._iterator.next(value);
        }

        if (playID === this._playID) {
            this._onStop(true, true);
        }
        return this._finishPromise;
    }

    /**
     * Stop the process if it is running.
     */
    public stop(waitForFinish = true) {
        if (!this.isRunning) {
            return;
        }
        if (this.isPaused) {
            this._resumeResolve();
            this._pausePromise = null;
        }
        this._stopResolve(Processor.STOP_SYMBOL);
        this._onStop(waitForFinish, false);
    }

    /**
     * Return a promise which will fullfill after the process stopped or finished.
     */
    public waitForFinish() {
        if (!this.isRunning) {
            return Promise.resolve();
        }
        return this._finishPromise;
    }

    public pause() {
        if (this.isPaused) {
            return;
        }
        this.emit(EventType.PAUSE, this.processorName);
        this._pausePromise = new Promise<void>((resolve) => {
            this._resumeResolve = resolve;
        });
    }

    public resume() {
        if (!this.isPaused) {
            return;
        }
        this.emit(EventType.RESUME, this.processorName);
        this._resumeResolve();
        this._pausePromise = null;
    }

    private async _onStop(waitForFinish: boolean, isEndNaturely: boolean) {
        if (!this.isRunning) {
            return;
        }
        if (!this._isStopping) {
            this._isStopping = true;
            this.emit(EventType.STOP, this.processorName, isEndNaturely);
            if (waitForFinish) {
                await this.finish(isEndNaturely);
                // already stopped forcibly
                if (!this._isStopping) {
                    return;
                }
            } else {
                this.finish(isEndNaturely);
            }
        } else if (waitForFinish) {
            return;
        }
        this._finishResolve();
        this._finishPromise = null;
        this._isStopping = false;
        this._playID++;
        this.emit(EventType.FINISH, this.processorName, isEndNaturely);
    }

    /** Write implementation of your process here. */
    protected abstract run(...params: any): Generator<Promise<any>, any, any>;

    /** Write implemetation of what should be done after the process stopped or finished. */
    protected abstract finish(isEndNaturely: boolean): Promise<void>;
}
